import { MouseEvent, ReactNode, SyntheticEvent, useRef, useState } from 'react';

import { Box } from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import { useModal } from 'mui-modal-provider';

import { ShowModalOutput } from '../../components-v2/dialogs/makeDialog';
import { TypographyOrNode } from '../../components-v2/utils';
import { tokens } from '../../theme/generated/tokens';
import { Button } from '../buttons';
import { HexIcon } from '../hex-icons';
import * as Icons from '../icons';
import { CloseDialogButton } from './CloseDialogButton';
import Dialog from './Dialog';
import { DialogFooter } from './DialogFooter';

type DisruptiveActionDialogProps = {
  title: ReactNode;
  body?: ReactNode;
  buttonText?: string;
  onProceed: (event: MouseEvent) => Promise<void> | void;
  onCancel?: (event?: SyntheticEvent) => void;
  onClick?: (event: MouseEvent) => void;
};

type FullProps = {
  destroy: () => void;
} & DisruptiveActionDialogProps;

const DisruptiveActionDialog = ({
  title,
  body = 'This action cannot be undone.',
  buttonText = 'Delete',
  onProceed,
  onCancel,
  onClick,
  destroy,
}: FullProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = (event?: SyntheticEvent) => {
    onCancel?.(event);
    destroy();
  };

  return (
    <Dialog
      onClose={handleCancel}
      open
      disableCloseOnBackdropClick
      testId="disruptive-action-dialog"
      onClick={onClick}
    >
      <CloseDialogButton />
      <DialogContent
        sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8 }}
      >
        <HexIcon
          variant="error"
          icon={<Icons.Trash />}
          size={34}
          sx={{ maxHeight: 32 }}
        />
        <Box
          display="flex"
          alignItems="center"
          data-testid="disruptive-action-title"
        >
          <TypographyOrNode variant="h4">{title}</TypographyOrNode>
        </Box>
        <Box gridColumn="2" data-testid="disruptive-action-body">
          <TypographyOrNode
            variant="P14R"
            color={tokens.theme.fg.structural.secondary}
          >
            {body}
          </TypographyOrNode>
        </Box>
      </DialogContent>
      <DialogFooter
        end={
          <>
            <Button
              onClick={handleCancel}
              variant="secondary"
              testId="cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={async (event: MouseEvent) => {
                try {
                  setIsLoading(true);
                  await onProceed(event);
                  if (!event.isDefaultPrevented()) {
                    destroy();
                  }
                } finally {
                  setIsLoading(false);
                }
              }}
              loading={isLoading}
              variant="danger"
              testId="proceed-button"
            >
              {buttonText}
            </Button>
          </>
        }
      />
    </Dialog>
  );
};

type DialogOptions = {
  rootId?: string;
};

/**
 * @deprecated Old dialog system — use `useDestructiveDialog` from `@cast/design-system`
 * (`libs/design-system/src/components-v2/dialogs/variants/destructive`) instead.
 * For custom destructive dialog content, use `makeDialog(DestructiveDialogContent)`.
 * To migrate a dialog that uses this, run the `/refactor-dialog <path>` skill.
 */
export const useDisruptiveActionDialog = (options: DialogOptions = {}) => {
  const modalRef = useRef<ShowModalOutput>(undefined);
  const { showModal } = useModal();

  const openDisruptiveActionDialog = (props: DisruptiveActionDialogProps) => {
    const modal = showModal(
      DisruptiveActionDialog,
      {
        destroy: () => modal.destroy(),
        ...props,
      },
      options
    );
    modalRef.current = modal;
  };

  return { openDisruptiveActionDialog };
};
